
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import * as accouttypes from '@account/store/types'
import api from '../../api'
import TemplateHeader from './TemplateHeader.vue'
import TemplateList from './TemplateList.vue'
import TemplateModal from '../modals/template-modal/TemplateModal.vue'
import { LoadingBar } from '@appbase/components/loading'

const getInitialTemplate = (comCode: string): webpartv3.WebpartTemplate => {
    return {
        pageId: 0,
        comCode: comCode,
        name: '',
        displayName: '',
        cols: 15,
        rows: 24,
        rowHeight: 130,
        designWidth: 1470,
        isDefault:false
    }
}

/**
 * 웹파트 템플릿를 추가/편집/삭제를 통해 관리하는 화면
 */
@Component({
    components: { TemplateHeader, TemplateList, TemplateModal, LoadingBar },
    computed: {
        ...mapGetters({
            current: accouttypes.CURRENT,
        }),
    },
})
export default class TemplateManagement extends Vue {
    /** 회사 코드 */
    @Prop({ type: String, required: true }) company!: string

    /** 현재 로그인된 사용자의 애플리케이션 데이터 */
    current!: account.TCurrent

    /** Webpart 목록 */
    templates: webpartv3.WebpartTemplate[] = []
    loading: boolean = false

    async created() {
        await this.refresh()
    }

    //계열사 변경 감지
    @Watch('company')
    companyChanged(){
        this.refresh();
    }
    /**
     * 웹파트 템플릿 목록을 새로고침합니다.
     */
    async refresh() {
        this.loading = true
        // 로그인된 사용자의 회사에서 만든 모든 웹파트 템플릿 목록 조회
        this.templates = await api.template.get(this.company)
        this.loading = false
    }

    /**
     * 웹파트 템플릿 수정을 위한 모달 오픈
     */
    handleCreateEdit(template?: webpartv3.WebpartTemplate) {
        const { company: comCode } = this
        const modal: appbase.IModalOpenable = this.$refs['modal'] as any
        const options: appbase.TModalInfo<any> = {
            component: TemplateModal,
            props: {
                company: comCode,
                mode: template ? 'edit' : 'new',
                // 모달에서 정보가 변경될 것이기 때문에
                // webpart 참조를 넘기는 것보다 clone해서 전달
                template: { ...(template || getInitialTemplate(comCode)) },
                title: template ? this.$t('웹파트_템플릿_수정') : this.$t('웹파트_템플릿_생성').toString()
            },
            events: {
                // 저장 버튼 클릭 이벤트 핸들러
                save: async (
                    _: TemplateModal,
                    template: webpartv3.WebpartTemplate
                ) => {
                    console.log('save', template, _)
                    if (await this.handleSave(template)) {
                        modal.closeModal() // 저장 성공시 모달 닫음
                    }
                },
                delete: async (template: webpartv3.WebpartTemplate) => {
                    if (await this.handleDelete(template)) {
                        // await this.refresh() <-- handleDelete 함수에서 새로고침함
                        modal.closeModal() // 삭제 성공시 모달 닫음
                    }
                },
                // 취소 버튼 클릭 이벤트 핸들러
                cancel: (template: webpartv3.WebpartTemplate) => {
                    console.log('cancel', template)
                    modal.closeModal()
                },
            },
        }
        modal.openModal(options)
    }

    async handleSave(template: webpartv3.WebpartTemplate): Promise<boolean> {
        try {
            const succeed = await api.template.save(template)
            if (succeed) {
                (window as any).alertAsync(this.$t('저장되었습니다').toString())

                // 웹파트 템플릿 목록 새로고침
                await this.refresh()
                return true
            }
        } catch (err) {
            console.error(err)
        }
        (window as any).alertAsync(this.$t('저장에_실패하였습니다').toString())
        return false
    }

    /**
     * 웹파트 템플릿를 삭제합니다.
     */
    async handleDelete(template: webpartv3.WebpartTemplate): Promise<boolean> {
        const message = this.$t('정말로_삭제하시겠습니까').toString()
        if (!await (window as any).confirmAsync(message)) return false
        if (!template.id) throw new Error('template.id is empty.')

        try {
            const succeed = await api.template.delete(template.id)
            if (succeed) {
                (window as any).alertAsync(this.$t('M_Common_Deleted').toString())

                // 웹파트 템플릿 목록 새로고침
                await this.refresh()
                return true
            }
        } catch (err) {
            console.error(err)
        }
        (window as any).alertAsync(this.$t('삭제에_실패하였습니다').toString())
        return false
    }
}
